<template>
  <div ref="windowRef" class="window shadowed" :style="`left: ${windowPosition.x}px; top: ${windowPosition.y}px`">
    <div v-if="slots.header" class="header" @mousedown="startDrag">
      <div class="title">
        <slot name="header" />
      </div>
      <button class="close" @mousedown.stop @click="close()">&times;</button>
    </div>

    <div class="body">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Coord } from '@/models/GenericInterfaces'
import { browserStorage } from '@/utils/browserStorage'
import { getDebouncer } from '@/utils/Debouncer'
import { clamp, getScrollbarWidth, getVerticalScrollbarHeight } from '@/utils/tools'
import { onMounted, onUnmounted, ref, useSlots } from 'vue'

const emit = defineEmits(['close'])
const slots = useSlots()

const props = withDefaults(defineProps<{ edgeMargin?: number }>(), { edgeMargin: 5 })

const draggingStartOffset = ref<Coord>()
const draggingStart = ref<Coord>()
const windowPosition = ref<Coord>({ x: 0, y: 0 })
const windowRef = ref<HTMLElement>()

onMounted(() => {
  const windowSize = getWindowSize()

  document.addEventListener('keydown', closeWindowListener)
  window.addEventListener('resize', resizeWindowListener)
  const defaultPosition: Coord = { x: window.innerWidth / 2 - windowSize.width / 2, y: window.innerHeight / 2 - windowSize.height / 2 }
  limitWindowPosition(browserStorage.get<Coord>('productTable-searchWindowPosition') || defaultPosition)
})

onUnmounted(() => {
  document.removeEventListener('keydown', closeWindowListener)
  window.removeEventListener('resize', resizeWindowListener)
})

const closeWindowListener = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    close()
  }
}

const close = () => {
  emit('close')
}

const startDrag = (e: MouseEvent) => {
  draggingStartOffset.value = { x: e.offsetX, y: e.offsetY }
  draggingStart.value = { x: e.clientX, y: e.clientY }

  document.addEventListener('mousemove', dragHandler)
  document.addEventListener(
    'mouseup',
    () => {
      document.removeEventListener('mousemove', dragHandler)
      limitWindowPosition(windowPosition.value)
      browserStorage.set('productTable-searchWindowPosition', windowPosition.value, 'sessionStorage')
    },
    { once: true },
  )
}

const dragHandler = (e: MouseEvent) => {
  if (draggingStartOffset.value) {
    if (e.clientX >= 0 && e.clientX <= window.innerWidth) {
      windowPosition.value.x = e.clientX - draggingStartOffset.value.x
    }
    if (e.clientY >= 0 && e.clientY <= window.innerHeight) {
      windowPosition.value.y = e.clientY - draggingStartOffset.value.y
    }
  }
}

const resizeWindowListener = getDebouncer(() => {
  limitWindowPosition(windowPosition.value)
}, 300)

const limitWindowPosition = (coord: Coord) => {
  const windowSize = getWindowSize()

  windowPosition.value = {
    x: clamp(coord.x, props.edgeMargin, window.innerWidth - windowSize?.width - getScrollbarWidth() - props.edgeMargin),
    y: clamp(coord.y, props.edgeMargin, window.innerHeight - windowSize?.height - getVerticalScrollbarHeight() - props.edgeMargin),
  }
}

const getWindowSize = () => {
  const rect = windowRef.value?.getBoundingClientRect()
  return { width: rect?.width || 0, height: rect?.height || 0 }
}
</script>

<style lang="scss" scoped>
.window {
  position: fixed;
  z-index: 1030;
  background-color: white;
  overflow: hidden;

  &.shadowed {
    box-shadow: 0 0 1.5em #0005;
  }

  .header,
  .body {
    padding: 1rem 1.5rem;
  }

  .header {
    display: flex;
    user-select: none;
    cursor: move;

    .title {
      display: flex;
      flex: 1;
      align-items: flex-end;
      pointer-events: none;
      font-size: 1.4rem;
    }

    .close {
      position: absolute;
      top: 0;
      right: 0;
      font-size: 1.8em;
      padding: 0.75em 0.5em;
      line-height: 0;
      font-weight: 100;
      user-select: none;
      outline: none;

      &:hover {
        background-color: #0002;
      }
    }
  }

  .body {
    padding-top: 0;
  }
}
</style>
